/**
 * Dropdown/list ordering.
 *
 * Every user-facing list of names (buildings, people, roles, vendors) reads
 * alphabetically by default. Case-insensitive and digit-aware, so "chase tower"
 * sits between "Ashley" and "Wells Fargo" rather than below both, and
 * "9 Marina" comes before "10 Park".
 *
 * Pinned entries ("All Buildings", "Unassigned", a "Select..." placeholder) are
 * NOT part of the sort — prepend/append them to the sorted list at the caller.
 */
const collator = new Intl.Collator('en', { sensitivity: 'base', numeric: true })

export const compareText = (a?: string | null, b?: string | null) => {
  const left = a == null ? '' : String(a)
  const right = b == null ? '' : String(b)
  // Unnamed rows sink to the bottom instead of leading the list.
  if (!left && !right) return 0
  if (!left) return 1
  if (!right) return -1
  return collator.compare(left, right)
}

/** Sorts a COPY of the list by `key` (default `label`), so shared/state arrays stay untouched. */
export function sortByLabel<T>(list: T[] | undefined | null, key = 'label'): T[] {
  if (!Array.isArray(list)) return []
  return [...list].sort((a, b) => compareText((a as any)?.[key], (b as any)?.[key]))
}

/** Same, for lists whose display name is nested (e.g. `Name.en` on an Entity). */
export function sortByPath<T>(list: T[] | undefined | null, path: string): T[] {
  if (!Array.isArray(list)) return []
  const read = (item: any) => path.split('.').reduce((acc, k) => (acc == null ? undefined : acc[k]), item)
  return [...list].sort((a, b) => compareText(read(a), read(b)))
}
